import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BusinessInformationEntity } from 'src/app/models/business.model';
import { PostEntity } from 'src/app/models/post.model';
import { BusinessService } from 'src/app/services/business/business.service';

declare const Swiper: any;

@Component({
  selector: 'app-blogrightsidebar',
  templateUrl: './blogrightsidebar.component.html',
  styleUrls: ['./blogrightsidebar.component.css'],
})
export class BlogrightsidebarComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() posts: PostEntity[] = [];
  storeMap: Record<string, BusinessInformationEntity> = {};
  private swiperInstance: any = null;
  private sub = new Subscription();

  constructor(private readonly businessService: BusinessService) {}

  get featuredStores(): PostEntity[] {
    const seen = new Set<string>();
    const unique: PostEntity[] = [];
    for (const post of this.posts || []) {
      if (!post.storeId || seen.has(post.storeId)) {
        continue;
      }
      seen.add(post.storeId);
      unique.push(post);
      if (unique.length >= 3) {
        break;
      }
    }
    return unique;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['posts']) {
      this.prefetchStores();
      setTimeout(() => this.initSwiper(), 0);
    }
  }

  ngAfterViewInit(): void {
    this.initSwiper();
  }

  ngOnDestroy(): void {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    this.sub.unsubscribe();
  }

  getStore(storeId?: string | null): BusinessInformationEntity | null {
    if (!storeId) return null;
    return this.storeMap[storeId] || null;
  }

  getStoreImage(post: PostEntity): string {
    const store = this.getStore(post.storeId);
    return (
      store?.banner ||
      store?.galleryPhotos?.[0] ||
      post.afterPhotoUrl ||
      post.beforePhotoUrl
    );
  }

  getStoreLogo(post: PostEntity): string {
    const store = this.getStore(post.storeId);
    return store?.storeLogo || post.userInfo?.photoUrl || 'assets/img/user/user-1.png';
  }

  private prefetchStores(): void {
    const storeIds = this.featuredStores
      .map((p) => p.storeId)
      .filter((id): id is string => !!id && !this.storeMap[id]);

    const uniqueIds = Array.from(new Set(storeIds));
    if (!uniqueIds.length) return;

    this.sub.add(
      forkJoin(
        uniqueIds.map((id) =>
          this.businessService.findById(id).pipe(
            catchError((err) => {
              console.error('Failed to load store', id, err);
              return of(null as BusinessInformationEntity | null);
            })
          )
        )
      ).subscribe((stores: Array<BusinessInformationEntity | null>) => {
        stores.forEach((store) => {
          if (store && store.id) {
            this.storeMap[store.id] = store;
          }
        });
        // Re-init swiper after stores are fetched to ensure arrows work
        setTimeout(() => this.initSwiper(), 0);
      })
    );
  }

  private initSwiper(): void {
    if (typeof Swiper === 'undefined' || !this.featuredStores.length) {
      return;
    }

    // Destroy existing instance before creating a new one
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    const swiperElement = document.querySelector('.advertisement-slider.swiper-container');
    if (swiperElement) {
      this.swiperInstance = new Swiper(swiperElement, {
        slidesPerView: 1,
        spaceBetween: 16,
        loop: this.featuredStores.length > 1,
        navigation: {
          nextEl: '.advertisement-slider .swiper-button-next',
          prevEl: '.advertisement-slider .swiper-button-prev',
        },
      });
    }
  }
}
